// src/pages/manager/production/ManagerCreateProduction.jsx
import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  TextField,
  MenuItem,
  Button,
  Box,
  Grid,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import api from "../../../utils/api";
import ManagerLayout from "../../../layouts/ManagerLayout";

const ManagerCreateProduction = () => {
  const managerUser = JSON.parse(localStorage.getItem("user"));
  const superAdminId = managerUser?.superAdminId;
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    items: [
      {
        product: "",
        unit: "",
        availableQty: 0,
        secondaryQuantity: 0,
        quantity: 0,
        productDetails: null,
      },
    ],
  });

  useEffect(() => {
    async function loadData() {
      const pRes = await api.get(`/api/products/get-all/${superAdminId}`);
      await api.put(`/api/reports/closing-stock/${superAdminId}`);
      const stockRes = await api.get(
        `/api/reports/closing-stock/${superAdminId}`
      );
      const stockMap = {};
      stockRes.data.forEach((s) => {
        stockMap[s.productName] = s;
      });
      setProducts(
        pRes.data.map((p) => ({
          ...p,
          closingQty: stockMap[p.productName]?.closingQty || 0,
          _secFactor: p.secondaryQuantity || 1,
        }))
      );
    }
    if (superAdminId) loadData();
  }, [superAdminId]);

  const handleItemChange = (idx, key, value) => {
    const items = [...form.items];
    const it = { ...items[idx] };

    if (key === "product") {
      const pd = products.find((p) => p.productName === value);
      if (pd) {
        it.product = pd.productName;
        it.unit = pd.primaryUnit;
        it.availableQty = pd.closingQty;
        it.productDetails = pd;
        it.quantity = 0;
        it.secondaryQuantity = 0;
      }
    } else if (key === "quantity") {
      it.quantity = value;
      it.secondaryQuantity = parseFloat((value / it._secFactor).toFixed(2));
    } else if (key === "secondaryQuantity") {
      it.secondaryQuantity = value;
      it.quantity = parseFloat((value * it._secFactor).toFixed(2));
    }

    items[idx] = it;
    setForm({ ...form, items });
  };

  const addRow = () =>
    setForm((f) => ({
      ...f,
      items: [
        ...f.items,
        {
          product: "",
          unit: "",
          availableQty: 0,
          secondaryQuantity: 0,
          quantity: 0,
          productDetails: null,
        },
      ],
    }));

  const removeRow = (idx) =>
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, i) => i !== idx),
    }));

  const handleSubmit = async () => {
    try {
      await api.put(`/api/reports/closing-stock/${superAdminId}`);
      await api.post(`/api/production/create`, {
        superAdminId,
        date: form.date,
        items: form.items.map((it) => ({
          product: it.product,
          unit: it.unit,
          primaryQuantity: it.quantity,
          secondaryQuantity: it.secondaryQuantity,
        })),
      });
      alert("Production recorded");
      navigate("/manager/production/view");
    } catch (err) {
      console.error(err);
      alert("Failed to record production");
    }
  };

  return (
    <ManagerLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Daily Production Entry
        </Typography>

        {/* Date Picker */}
        {isMobile ? (
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12}>
              <TextField
                type="date"
                label="Date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
          </Grid>
        ) : (
          <Box display="flex" gap={2} mb={3}>
            <TextField
              type="date"
              label="Date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 200 }}
            />
          </Box>
        )}

        {/* Items List */}
        {isMobile ? (
          form.items.map((it, idx) => (
            <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <TextField
                    select
                    label="Product"
                    size="small"
                    value={it.product}
                    onChange={(e) =>
                      handleItemChange(idx, "product", e.target.value)
                    }
                    fullWidth
                  >
                    {products.map((p) => (
                      <MenuItem key={p._id} value={p.productName}>
                        {p.productName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Unit"
                    size="small"
                    value={it.unit}
                    InputProps={{ readOnly: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Avail Stock"
                    size="small"
                    value={it.availableQty}
                    InputProps={{ readOnly: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Primary Qty"
                    type="number"
                    size="small"
                    value={it.quantity}
                    onChange={(e) =>
                      handleItemChange(idx, "quantity", Number(e.target.value))
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Secondary Qty"
                    type="number"
                    size="small"
                    value={it.secondaryQuantity}
                    onChange={(e) =>
                      handleItemChange(
                        idx,
                        "secondaryQuantity",
                        Number(e.target.value)
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} textAlign="right">
                  <IconButton onClick={() => removeRow(idx)}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ mb: 3, overflowX: "auto" }}
          >
            <Table sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableBody>
                {form.items.map((it, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ width: "20%" }}>
                      <TextField
                        select
                        size="small"
                        value={it.product}
                        onChange={(e) =>
                          handleItemChange(idx, "product", e.target.value)
                        }
                        fullWidth
                      >
                        {products.map((p) => (
                          <MenuItem key={p._id} value={p.productName}>
                            {p.productName}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ width: "16%" }}>{it.unit}</TableCell>
                    <TableCell sx={{ width: "16%" }}>
                      {it.availableQty}
                    </TableCell>
                    <TableCell sx={{ width: "16%" }}>
                      <TextField
                        type="number"
                        size="small"
                        value={it.quantity}
                        onChange={(e) =>
                          handleItemChange(
                            idx,
                            "quantity",
                            Number(e.target.value)
                          )
                        }
                        fullWidth
                      />
                    </TableCell>
                    <TableCell sx={{ width: "16%" }}>
                      <TextField
                        type="number"
                        size="small"
                        value={it.secondaryQuantity}
                        onChange={(e) =>
                          handleItemChange(
                            idx,
                            "secondaryQuantity",
                            Number(e.target.value)
                          )
                        }
                        fullWidth
                      />
                    </TableCell>
                    <TableCell sx={{ width: "16%" }}>
                      <IconButton onClick={() => removeRow(idx)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Footer */}
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          justifyContent="space-between"
          mb={3}
          gap={2}
        >
          <Button variant="outlined" onClick={addRow} fullWidth={isMobile}>
            + Add Row
          </Button>
        </Box>

        <Box textAlign="center">
          <Button
            variant="contained"
            onClick={handleSubmit}
            fullWidth={isMobile}
          >
            Submit Production
          </Button>
        </Box>
      </Container>
    </ManagerLayout>
  );
};

export default ManagerCreateProduction;
